import { useEffect, useState, createContext, useContext } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Ensure cookies are sent with every request
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);
  const value = {
    user,
    setUser,
    logout: async () => {
      try {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      } catch (_) {}
      setUser(false);
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.5)", background: "#0a0a0a", fontFamily: "system-ui" }}>
        Verificando sesión...
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Error inesperado. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)", display: "grid", placeItems: "center", padding: 20, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <form onSubmit={submit} style={{ width: "100%", maxWidth: 400, background: "rgba(20,20,30,0.8)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 36, backdropFilter: "blur(24px)" }} data-testid="login-form">
        <div style={{ fontSize: 28, fontWeight: 700, color: "#fff", marginBottom: 6, letterSpacing: "-0.02em" }}>GhostBrand</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 28 }}>Admin access only</div>

        <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.5)", marginBottom: 6 }}>Email</label>
        <input
          data-testid="login-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ width: "100%", padding: "12px 14px", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 14, marginBottom: 20, outline: "none", boxSizing: "border-box" }}
          placeholder="you@example.com"
        />

        <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.5)", marginBottom: 6 }}>Password</label>
        <input
          data-testid="login-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ width: "100%", padding: "12px 14px", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 14, marginBottom: 24, outline: "none", boxSizing: "border-box" }}
          placeholder="••••••••••••"
        />

        {error && (
          <div data-testid="login-error" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171", padding: "10px 12px", borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <button
          data-testid="login-submit"
          type="submit"
          disabled={loading}
          style={{ width: "100%", padding: "12px 14px", background: loading ? "rgba(99,102,241,0.4)" : "#6366f1", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", transition: "background 0.2s" }}
        >
          {loading ? "Accediendo..." : "Acceder"}
        </button>

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <a href="/" data-testid="login-back-home" style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>← Volver al inicio</a>
        </div>
      </form>
    </div>
  );
}

export function LandingPage() {
  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", color: "#fff", fontFamily: "system-ui, -apple-system, sans-serif", display: "flex", flexDirection: "column" }}>
      <header style={{ padding: "24px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em" }} data-testid="landing-logo">GhostBrand</div>
        <a
          href="/login"
          data-testid="landing-login-link"
          style={{ padding: "8px 18px", background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.4)", color: "#a5b4fc", borderRadius: 8, textDecoration: "none", fontSize: 13, fontWeight: 500 }}
        >
          Admin Login
        </a>
      </header>

      <main style={{ flex: 1, display: "grid", placeItems: "center", padding: "60px 40px" }}>
        <div style={{ maxWidth: 760, textAlign: "center" }}>
          <div style={{ display: "inline-block", padding: "6px 14px", background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: 20, fontSize: 12, color: "#a5b4fc", marginBottom: 24, letterSpacing: "0.04em" }}>
            AUTONOMOUS HOLDING COMPANY
          </div>
          <h1 style={{ fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 700, lineHeight: 1.05, margin: "0 0 20px", letterSpacing: "-0.03em" }}>
            Mathematically exact.<br />
            <span style={{ color: "#a5b4fc" }}>Autonomously profitable.</span>
          </h1>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.65)", margin: "0 0 36px", lineHeight: 1.6, maxWidth: 580, marginLeft: "auto", marginRight: "auto" }}>
            GhostBrand operates a portfolio of content businesses across 18+ verticals. Every decision — from budget allocation to content cadence — is derived from real-time data using Bayesian consensus and Kelly-optimal allocation.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginTop: 48 }}>
            {[
              { label: "Verticals", value: "18+" },
              { label: "Content Channels", value: "Dev.to / Hashnode" },
              { label: "Decision Engine", value: "Bayesian + Kelly" },
              { label: "Operation", value: "24/7 autonomous" },
            ].map((s) => (
              <div key={s.label} data-testid={`landing-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`} style={{ padding: 20, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 60, padding: 24, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 12, textAlign: "left" }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.4)", marginBottom: 10 }}>Contact</div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }}>
              Business inquiries: <a href="mailto:ceo@ghostbrand.lat" style={{ color: "#a5b4fc", textDecoration: "none" }}>ceo@ghostbrand.lat</a>
            </div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", marginTop: 4 }}>
              Partner support: <a href="mailto:service@ghostbrand.lat" style={{ color: "#a5b4fc", textDecoration: "none" }}>service@ghostbrand.lat</a>
            </div>
          </div>
        </div>
      </main>

      <footer data-testid="landing-footer" style={{ padding: "20px 40px", fontSize: 12, color: "rgba(255,255,255,0.4)", borderTop: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>
        GhostBrand Engine — autonomous holding company. Affiliate partnerships via impact.com, Digistore24, Gumroad, ClickBank. © 2026
      </footer>
    </div>
  );
}
